// Single source of truth for competition seasons.
//
// Plain CommonJS so both sides of the site can load it: the browser (via
// src/config/seasons.js, bundled by CRA) and the Vercel functions in api/
// (via api/_lib/seasons.js). Keep this file free of browser-only or
// Node-only code.
//
// To open/close a season: adjust opensAt / closesAt on its entry.
// To add a new season: append a new entry to SEASONS (copy the latest one —
// dates, price cutoffs and `prices`). It is safe to add it before it opens —
// until opensAt the site shows "Registration opens <date>". Full list of steps:
// docs/new_season_checklist.md.
//
// A season moves through four states (getSeasonState):
//   coming_soon  now < opensAt               "Registration opens <date>"
//   open         opensAt <= now <= closesAt  normal register flow
//   pre_event    closesAt < now <= eventDate countdown, "come cheer them on"
//   post_event   now > eventDate             thanks + results, next season
//
// Dates are ISO strings with an explicit offset (Eastern time, since the
// competition is in Toronto). The gate is date-driven — no manual flag flipping.

const CONTACT_EMAIL = 'info@prixducanada.com';

// The month the next season's registration usually opens, in the year of the
// latest season ("in November 2027" for 2028). Used only until that season is
// added to SEASONS (after which its opensAt date is shown instead — see
// getNextRegistration). Prose rather than a date: it stays deliberately soft.
const NEXT_SEASON_OPENS_MONTH = 'November';

// From this season on, registration is a cart: a one-time registration fee
// plus solo / ensemble entries paid as they're added. Earlier seasons were a
// flat fee per registration (see pricing.quote()).
const CART_SEASONS_FROM = 2027;
const usesCart = (year) => Number(year) >= CART_SEASONS_FROM;

const SEASONS = [
  {
    year: 2026,
    opensAt:  '2026-03-01T00:00:00-05:00',
    closesAt: '2026-09-06T23:59:59-04:00', // Sept 6, 2026, 11:59 PM Eastern
    eventDate: '2026-09-20T23:59:59-04:00', // Competition day (Sandra Faire). After this, the season is "past".
    stageStartsAt: '2026-09-20T10:00:00-04:00', // First dancer on stage; the Home countdown targets this.
    venue: 'Sandra Faire and Ivan Fecan Theatre, York University, Toronto',
  },
  {
    year: 2027,
    // Open as soon as this branch ships: merging to main is the launch.
    // closesAt, eventDate, stageStartsAt and venue are not decided yet.
    opensAt: '2026-11-01T00:00:00-04:00',
    closesAt: null,
    eventDate: null,
    stageStartsAt: null,
    venue: null,
    // Shown on the FAQ, Rules and Home until eventDate / venue are set.
    eventWhen: 'A weekend in the second half of September 2027',
    venueNote: 'To be announced in spring 2027',
    // Price cutoffs (api/_lib/pricing.js): end of day Eastern.
    earlyBirdEndsAt: '2027-01-31T23:59:59-05:00', // registration fee $150 until then
    comboEndsAt: '2027-03-31T23:59:59-04:00',     // solo + ensemble combo until then
    // CAD before HST. See docs/2027_registration_rules.md → "Prices".
    prices: {
      registration: 180,
      registrationEarlyBird: 150, // until earlyBirdEndsAt
      solo: 160,
      ensemble: 80,               // per dancer
      combo: 220,                 // 1 solo + 1 ensemble, until comboEndsAt
    },
    // Schools: regular prices above (no early bird, no combo), with a discount
    // on the whole order by its pre-tax total. Estimates in the portal; schools
    // are invoiced by the office. Highest tier the total reaches wins.
    schoolPricing: {
      tiers: [
        { name: 'Bronze', from: 2000, percentOff: 10 },
        { name: 'Silver', from: 4000, percentOff: 15 },
        { name: 'Gold',   from: 6000, percentOff: 20 },
      ],
    },
  },
];

// Testers who can use a season before its opensAt (everyone else sees
// "Registration opens on <date>"). The checkout and team APIs check this
// too. Emails are matched case-insensitively.
const EARLY_ACCESS_EMAILS = {
  2027: ['minjia.bianca.zhu@gmail.com', 'info@prixducanada.com', 'minjia.zhu@bravecareer.io', 'riho.dantow@gmail.com'],
};

// Per-user late-access exceptions. Each entry lets ONE specific email
// register past the season's closesAt, until the given deadline. Used only
// for cases where the dancer notified us in advance and we approved an
// extension — not a general reopening. Emails are matched case-insensitively.
const LATE_ACCESS_OVERRIDES = [
  {
    year: 2026,
    email: 'ymsusansit@gmail.com',
    until: '2026-09-09T23:59:59-04:00', // 2-day extension (Luella Lam)
  },
  {
    year: 2026,
    email: 'info@prixducanada.com',
    until: '2026-09-07T23:59:59-04:00', // 2-day extension (Test)
  },
];

/** The raw SEASONS entry for a year, or undefined. */
function getSeason(year) {
  return SEASONS.find((s) => s.year === year);
}

/**
 * The registration deadline for a year, formatted for display. Derived from
 * closesAt so it can never drift from the gate the way a hardcoded date does.
 */
function getDeadlineLabel(year) {
  const season = getSeason(year);
  if (!season?.closesAt) return 'the posted deadline';
  return new Date(season.closesAt).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Toronto',
  });
}

/** Competition day for a year, formatted for display, or null if not decided. */
function getEventDateLabel(year) {
  const season = getSeason(year);
  if (!season?.eventDate) return null;
  return new Date(season.eventDate).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Toronto',
  });
}

/**
 * State of a given competition year, based on `now`.
 * Returns: 'coming_soon' | 'open' | 'pre_event' | 'post_event' | 'unknown'
 */
function getSeasonState(year, now = new Date()) {
  const season = getSeason(year);
  if (!season) return 'unknown';
  if (now < new Date(season.opensAt)) return 'coming_soon';
  if (!season.closesAt || now <= new Date(season.closesAt)) return 'open';
  if (!season.eventDate || now <= new Date(season.eventDate)) return 'pre_event';
  return 'post_event';
}

/**
 * The season the site is "about" right now, and that new registrations are
 * created under. In priority order:
 *  - the open season;
 *  - a season between close and competition day (dancers about to perform);
 *  - the next season that hasn't opened yet (pre-open messaging);
 *  - otherwise the most recent season (archive messaging).
 */
function getActiveSeason(now = new Date()) {
  const inState = (state) => SEASONS.filter((s) => getSeasonState(s.year, now) === state);
  const byOpensAt = (a, b) => new Date(a.opensAt) - new Date(b.opensAt);

  const open = inState('open')[0];
  if (open) return open;
  const preEvent = inState('pre_event')[0];
  if (preEvent) return preEvent;
  const upcoming = inState('coming_soon').sort(byOpensAt)[0];
  if (upcoming) return upcoming;
  return SEASONS[SEASONS.length - 1];
}

/** The most recent season whose competition day has passed, or undefined. */
function getLastHeldSeason(now = new Date()) {
  return SEASONS
    .filter((s) => getSeasonState(s.year, now) === 'post_event')
    .sort((a, b) => b.year - a.year)[0];
}

/**
 * The next registration to open, for "registration opens …" copy.
 * `opensPhrase` reads after "opens": "on March 1, 2027" once that season is in
 * SEASONS, otherwise "in November <latest season's year>".
 */
function getNextRegistration(now = new Date()) {
  const upcoming = SEASONS
    .filter((s) => getSeasonState(s.year, now) === 'coming_soon')
    .sort((a, b) => new Date(a.opensAt) - new Date(b.opensAt))[0];
  if (upcoming) {
    const date = new Date(upcoming.opensAt).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Toronto',
    });
    return { year: upcoming.year, opensPhrase: `on ${date}` };
  }
  const latest = SEASONS[SEASONS.length - 1];
  return { year: latest.year + 1, opensPhrase: `in ${NEXT_SEASON_OPENS_MONTH} ${latest.year}` };
}

function isRegistrationOpen(year, now = new Date()) {
  return getSeasonState(year, now) === 'open';
}

/**
 * True once a season's competition day has passed. Used by the profile to
 * move a registration from "current" into "Past Competitions" (where the
 * judging report lives). A year not in SEASONS but older than every known
 * season is treated as past.
 */
function isSeasonPast(year, now = new Date()) {
  const season = getSeason(year);
  if (!season) return SEASONS.every((s) => s.year > year);
  if (!season.eventDate) return false;
  return now > new Date(season.eventDate);
}

// Accounts that see the post-event profile view ("Past Competitions" + report
// button) BEFORE the season's eventDate. For testing the results flow on a
// real account. Display-only: judging reports are still gated by RLS on
// published_at, so this never exposes unreleased data.
const PAST_PREVIEW_EMAILS = [
  // e.g. 'minjia.bianca.zhu@gmail.com' — add while testing, remove after.
];

/**
 * Same as isSeasonPast(), but also true for PAST_PREVIEW_EMAILS so a tester
 * can exercise the past-season UI ahead of the event date.
 */
function isSeasonPastForUser(year, email, now = new Date()) {
  if (isSeasonPast(year, now)) return true;
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return PAST_PREVIEW_EMAILS.some((e) => e.toLowerCase() === normalized);
}

/**
 * Same as isRegistrationOpen(), but also honors EARLY_ACCESS_EMAILS (testers,
 * before opensAt) and LATE_ACCESS_OVERRIDES (a pre-approved user finishing
 * after the season closes). Falls back to the standard check when no email
 * is provided. The server uses this as its gate for checkout and team actions.
 */
function isRegistrationOpenForUser(year, email, now = new Date()) {
  if (isRegistrationOpen(year, now)) return true;
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  if (getSeasonState(year, now) === 'coming_soon'
      && (EARLY_ACCESS_EMAILS[year] || []).some((e) => e.toLowerCase() === normalized)) {
    return true;
  }
  const override = LATE_ACCESS_OVERRIDES.find(
    (o) => o.year === year && o.email.toLowerCase() === normalized
  );
  if (!override) return false;
  return now <= new Date(override.until);
}

module.exports = {
  CONTACT_EMAIL,
  NEXT_SEASON_OPENS_MONTH,
  CART_SEASONS_FROM,
  usesCart,
  SEASONS,
  EARLY_ACCESS_EMAILS,
  LATE_ACCESS_OVERRIDES,
  PAST_PREVIEW_EMAILS,
  getSeason,
  getDeadlineLabel,
  getEventDateLabel,
  getSeasonState,
  getActiveSeason,
  getLastHeldSeason,
  getNextRegistration,
  isRegistrationOpen,
  isSeasonPast,
  isSeasonPastForUser,
  isRegistrationOpenForUser,
};
